<template>
  <b-sidebar id="sidebar-account-add-payment" sidebar-class="sidebar-lg" bg-variant="white" shadow backdrop no-header right>
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">Add Payment</h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- Body -->
      <b-form class="p-2" @submit.prevent>
        <!-- Payment Date -->
        <b-form-group label="Payment Date (scheduled for)" label-for="payment-date">
          <flat-pickr
            v-model="addPaymentData.date"
            class="form-control"
            :config="{ altInput: true, altFormat: 'F j, Y', dateFormat: 'Z' }"
          />
        </b-form-group>

        <!-- Payment Amount -->
        <b-form-group label="Payment Amount" label-for="payment-amount">
          <cleave
            id="payment-amount"
            v-model="addPaymentData.amount"
            class="form-control"
            :raw="true"
            :options="{ numeral: true, numeralThousandsGroupStyle: 'thousand' }"
            required
          />
        </b-form-group>

        <!-- Internal Payment Note -->
        <b-form-group label="Internal Payment Note" label-for="internal-payment-note">
          <b-form-input
            id="internal-payment-description"
            v-model="addPaymentData.description"
            placeholder="Payment Description"
            trim
          />
        </b-form-group>

        <b-form-group label="Payment Type" label-for="payment-type">
          <v-select
            v-model="addPaymentData.type"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="paymentTypeOptions"
            placeholder="Select Payment Type"
            input-id="payment-status"
            :reduce="(val) => val.value"
            :clearable="false"
          />
        </b-form-group>

        <b-form-group label="Payment Status" label-for="payment-status">
          <v-select
            v-model="addPaymentData.status"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="paymentStatusOptions"
            placeholder="Select Payment Status"
            input-id="payment-status"
            :reduce="(val) => val.value"
            :clearable="false"
          />
        </b-form-group>

        <!-- Payment Date -->
        <b-form-group label="Paid At Date" label-for="payment-date" v-if="addPaymentData.status === 'paid'">
          <flat-pickr
            v-model="addPaymentData.paidAt"
            class="form-control"
            :config="{ altInput: true, altFormat: 'F j, Y', dateFormat: 'Z' }"
          />
        </b-form-group>

        <!-- Form Actions -->
        <div class="d-flex mt-2">
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-2"
            type="submit"
            @click="addPayment"
            :disabled="isLoading"
          >
            Create
          </b-button>
          <b-button v-ripple.400="'rgba(186, 191, 199, 0.15)'" variant="outline-secondary" @click="hide"> Cancel </b-button>
        </div>
      </b-form>
    </template>
  </b-sidebar>
</template>

<script>
import { BSidebar, BForm, BFormGroup, BFormInput, BFormTextarea, BButton } from 'bootstrap-vue';
import { ref } from '@vue/composition-api';
import Ripple from 'vue-ripple-directive';
import flatPickr from 'vue-flatpickr-component';
import vSelect from 'vue-select';
import Cleave from 'vue-cleave-component';
import useAccountsList from '../account-list/useAccountsList';
import store from '@/store';
import receivableStoreModule from '@/views/apps/accounting/receivable/receivableStoreModule';
import { onUnmounted } from '@vue/composition-api/dist/vue-composition-api';
import ToastificationContent from '@core/components/toastification/ToastificationContent';

import { useToast } from 'vue-toastification/composition';
export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,

    Cleave,
    flatPickr,
    vSelect,
  },
  props: {
    accountId: {
      type: String,
    },
  },
  directives: {
    Ripple,
  },
  setup(props, { emit }) {
    const ACCOUNTING_APP_STORE_MODULE_NAME = 'app-accounting-receivable';

    // Register module
    if (!store.hasModule(ACCOUNTING_APP_STORE_MODULE_NAME))
      store.registerModule(ACCOUNTING_APP_STORE_MODULE_NAME, receivableStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(ACCOUNTING_APP_STORE_MODULE_NAME)) store.unregisterModule(ACCOUNTING_APP_STORE_MODULE_NAME);
    });

    const { paymentTypeOptions, paymentStatusOptions } = useAccountsList();
    const toast = useToast();

    const isLoading = ref(false);

    const addPaymentData = ref({
      date: new Date(),
      description: `Manual Payment`,
      amount: 0,
      paidAt: undefined,
      type: 'tuition',
      status: 'unpaid',
    });

    const addPayment = async () => {
      if (addPaymentData.value.paidAt && new Date(addPaymentData.value.paidAt) > new Date()) {
        return toast({
          component: ToastificationContent,
          props: {
            title: 'Error submitting account payment',
            text: 'Payments (in paid state) cannot be set in the future',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }

      isLoading.value = true;

      store
        .dispatch('app-accounting-receivable/addPaymentToAccount', {
          accountId: props.accountId,
          paymentData: addPaymentData.value,
        })
        .then((response) => {
          emit('hide');
          isLoading.value = false;
        })
        .catch(() => {
          isLoading.value = false;
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error submitting account payment',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          });
        });
    };

    return {
      paymentTypeOptions,
      paymentStatusOptions,
      addPaymentData,
      addPayment,
      isLoading,
    };
  },
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
